// invest_FE/src/context/BannerContext.js
import React, { createContext, useState, useCallback, useContext } from 'react';
import { v4 as uuidv4 } from 'uuid';

export const BannerContext = createContext();

export const useBanner = () => useContext(BannerContext);

export const BannerProvider = ({ children }) => {
  const [banners, setBanners] = useState([]);

  /**
   * Adds a new banner to be displayed.
   * @param {string|React.ReactNode} message - The message content of the banner.
   * @param {string} type - The type of banner ('info', 'warning', 'error', 'success').
   * @param {Array} [actions=[]] - Array of action objects { text: 'Click Me', onClick: () => {} }.
   * @param {string} [id=uuidv4()] - Optional unique ID for the banner. If not provided, one is generated.
   * @param {function} [onDismiss=null] - Optional callback to run when the banner is dismissed.
   * @returns {string} The ID of the added banner.
   */
  const addBanner = useCallback((message, type = 'info', actions = [], id = uuidv4(), onDismiss = null) => {
    setBanners((prevBanners) => {
      // Avoid adding a banner with the same ID twice
      if (prevBanners.some((banner) => banner.id === id)) {
        return prevBanners;
      }
      // Add the onDismiss callback to the banner's data
      return [...prevBanners, { id, message, type, actions, onDismiss }];
    });
    return id;
  }, []);

  /**
   * Removes a banner by its ID, triggering an optional onDismiss callback.
   * @param {string} id - The unique ID of the banner to remove.
   */
  const removeBanner = useCallback((id) => {
    setBanners((prevBanners) => {
      const bannerToRemove = prevBanners.find((b) => b.id === id);
      if (bannerToRemove?.onDismiss) {
        bannerToRemove.onDismiss(); // Trigger the custom dismiss logic if it exists
      }
      return prevBanners.filter((banner) => banner.id !== id);
    });
  }, []);

  const value = { banners, addBanner, removeBanner };

  return <BannerContext.Provider value={value}>{children}</BannerContext.Provider>;
};
